import { useState } from 'react';
import { Bell } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from '@/integrations/supabase/client';

export const EmailSubscribe = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('email_subscriptions')
        .insert([{ email, source: 'compare-page' }]);

      // 23505 = unique_violation (duplicate email) — treat as success
      if (error && error.code !== '23505') throw error;

      if (typeof window.gtag === 'function') {
        window.gtag('event', 'email_signup', { source: 'compare-page' });
      }
      toast({
        title: "You're subscribed!",
        description: "We'll alert you to new deals and discounts.",
      });
      setEmail('');
    } catch (error) {
      console.error('Error subscribing:', error);
      toast({
        title: "Error",
        description: "Failed to subscribe. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-brand-lightBlue dark:bg-gray-800 rounded-lg p-6 md:p-8 shadow-sm dark:shadow-md dark:shadow-black/20 border border-transparent dark:border-gray-700">
      <div className="mb-6 text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brand-blue text-white mb-4 dark:bg-blue-600 dark:shadow-inner">
          <Bell className="h-6 w-6" />
        </div>
        <h3 className="text-xl md:text-2xl font-bold text-brand-darkBlue dark:text-white mb-2">
          Get Alerts
        </h3>
        <p className="text-gray-600 dark:text-gray-300">
          Stay informed about the best prop firm deals, discounts, and promotions.
        </p>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Input 
            type="text" 
            placeholder="Your Name (Optional)" 
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400"
          />
        </div>
        <div>
          <Input 
            type="email" 
            placeholder="Your Email Address" 
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400"
          />
        </div>
        <Button 
          type="submit" 
          className="w-full bg-brand-blue hover:bg-brand-darkBlue dark:bg-blue-600 dark:hover:bg-blue-700 dark:text-white transition-colors"
          disabled={loading}
        >
          {loading ? (
            <span className="flex items-center">
              <span className="animate-spin mr-2">○</span>
              <span>Subscribing...</span>
            </span>
          ) : (
            <span>Subscribe Now</span>
          )}
        </Button>
        <p className="text-xs text-center text-gray-500 dark:text-gray-400">
          We respect your privacy and will never share your information.
          You can unsubscribe at any time.
        </p>
      </form>
    </div>
  );
};
